import api, { apiBaseUrl } from '../api/axios';
const API_BASE = apiBaseUrl.replace(/\/$/, "");

/**
 * Converts stored cloud storage keys (tenant-xxx/customers/123/profile.jpg) or legacy /uploads/ paths
 * into authenticated API viewer URLs.
 * Also passes through base64 data URLs unchanged.
 */
/**
 * Helper to retrieve stored auth token from localStorage or sessionStorage.
 *
 * @function getToken
 * @returns {string} Bearer token or empty string.
 */
const getToken = () => {
  try {
    const authHeader = api?.defaults?.headers?.common?.['Authorization'] || api?.defaults?.headers?.common?.['authorization'] || '';
    if (authHeader && authHeader.startsWith('Bearer ')) {
      return authHeader.substring(7);
    }
    return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  } catch (e) {
    return "";
  }
};

/**
 * Converts stored cloud storage keys or uploads relative paths into authenticated API viewer URLs.
 * Passes data/blob URLs directly and handles cache-busting query params.
 *
 * @function imgUrl
 * @param {string} path - Image file path, S3 key, data URI, or external URL.
 * @param {string|number} [cacheBustParam] - Optional timestamp for cache busting.
 * @returns {string|null} Resolved authenticated URL string or null if empty path.
 */
export function imgUrl(path, cacheBustParam) {
  if (!path || typeof path !== "string") return null;

  if (path.startsWith("data:") || path.startsWith("blob:")) {
    return path;
  }

  // Extract cache-busting parameter if attached
  let cacheBust = cacheBustParam ? String(cacheBustParam) : "";
  const tMatch = path.match(/[?&]t=(\d+)/);
  if (tMatch) {
    if (!cacheBust) cacheBust = tMatch[1];
    path = path.replace(/[?&]t=\d+/, "");
  }

  // Normalize backslashes to forward slashes and remove leading slash
  path = path.replace(/\\/g, "/").replace(/^\/+/, "");

  const token = getToken();

  let baseUrl = "";

  // Handle cloud storage keys (tenant-..., customers/..., checks/..., stores/..., uploads/...)
  // or a naked tenant CUID (e.g. cmtfdzbc100009mpc86zjkr94/checks/...)
  if (
    path.startsWith("tenant-") ||
    path.startsWith("customers/") ||
    path.startsWith("checks/") ||
    path.startsWith("stores/") ||
    path.startsWith("documents/") ||
    path.startsWith("uploads/") ||
    path.match(/^c[a-z0-9]{24}\//)
  ) {
    baseUrl = `${API_BASE}/api/files/view?key=${encodeURIComponent(path)}`;
  } else {
    // If path contains /uploads/, convert to authenticated /api/files/view
    const uploadsIdx = path.indexOf("/uploads/");
    if (uploadsIdx !== -1) {
      const relPath = path.substring(uploadsIdx + 9);
      baseUrl = `${API_BASE}/api/files/view?key=${encodeURIComponent(relPath)}`;
    } else {
      // Fix protocol if https://localhost was used in local environment
      if (path.startsWith("https://localhost") || path.startsWith("https://127.0.0.1")) {
        path = path.replace(/^https:/, "http:");
      }

      if (path.startsWith("http://") || path.startsWith("https://")) {
        baseUrl = path;
      } else {
        baseUrl = `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
      }
    }
  }

  // Append token and cache bust cleanly
  if (token) {
    baseUrl += (baseUrl.includes("?") ? "&" : "?") + `token=${encodeURIComponent(token)}`;
  }
  if (cacheBust) {
    baseUrl += (baseUrl.includes("?") ? "&" : "?") + `t=${encodeURIComponent(cacheBust)}`;
  }

  return baseUrl;
}

export default API_BASE;
